<template>
  <tr class="payments-row">
    <td class="center-align">
      {{
        types[item.agreement_type] +
        (["msa_contractor", "msa_crew"].includes(item.agreement_type)
          ? ", " + item.msa_year
          : "")
      }}
    </td>
    <td class="center-align">
      {{
        item.gigs && item.gigs.length
          ? item.gigs.map((gig) => gig.title).join(", ")
          : "N/A"
      }}
    </td>
    <td
      class="agreement__status center-align"
      :class="`agreement__status--${item.status}`"
    >
      <span>{{ statuses[item.status] }}</span>
      <span v-if="item.email_viewed_at || item.documents_viewed_at">
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" size="18">$eye</v-icon>
          </template>
          <div>
            <div v-if="item.documents_viewed_at">
              Documents viewed at
              {{ item.documents_viewed_at | moment("hh:mm, dddd M/D/YYYY") }}
            </div>
            <div v-else-if="item.email_viewed_at">
              Email viewed at
              {{ item.email_viewed_at | moment("hh:mm, dddd M/D/YYYY") }}
            </div>
          </div>
        </v-tooltip>
      </span>
      <span v-if="item.name_changed_at">
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" size="15">$pencil</v-icon>
          </template>
          <div>
            Name Changed
            {{ item.name_changed_to ? "to " + item.name_changed_to : "" }}
          </div>
        </v-tooltip>
      </span>
    </td>
    <td class="agreement__signed_at center-align">
      {{ signedAtFormatted(item) }}
    </td>
    <td>
      <TableRowAvatar :user="item.requested_by" />
    </td>
    <td class="agreement__requested_at center-align">
      {{ item.created_at | moment("timezone", currentTimezone, "MMM D, YYYY") }}
    </td>
    <td class="center-align">
      <ActionsMenu
        class="payments-row__actions-toggle"
        @close="menu = false"
        @toggle="menu = !menu"
        :menu="menu"
        :themeDisabledPage="themeDisabledPage"
        v-if="['CREATED', 'OUT_FOR_SIGNATURE', 'SIGNED'].includes(item.status)"
        :inertProp="menu || inertProp"
      >
        <v-btn
          v-if="canCancel"
          @click="cancelAgreement(item)"
          color="#262729"
          dark
          elevation="0"
        >
          Cancel Request
        </v-btn>
        <v-btn
          v-if="!item.has_signed_upload && item.status == 'SIGNED'"
          @click="downloadPdf(item)"
          color="#262729"
          dark
          elevation="0"
        >
          Download PDF
        </v-btn>
        <v-btn
          v-if="item.has_signed_upload && item.status == 'SIGNED'"
          @click="downloadPdf(item)"
          color="#262729"
          dark
          elevation="0"
        >
          Download
        </v-btn>
        <v-tooltip left max-width="300">
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                v-if="item.status == 'OUT_FOR_SIGNATURE'"
                :disabled="item.check_signing_url"
                @click="getSigningUrl(item)"
                color="primary"
                dark
                elevation="0"
                class="sign-btn"
              >
                Sign
              </v-btn>
            </span>
          </template>
          <span>
            Please check your email for a link to sign your agreement. If you
            haven't received it after a few minutes, contact us.
          </span>
        </v-tooltip>
      </ActionsMenu>
    </td>
    <PleaseWaitDialog :dialog="pleaseWait" />
  </tr>
</template>

<script>
import themeDisabledPageMixin from "@/mixins/theme-disabled-page.mixin";
import paymentsTableFormats from "@/mixins/payments-table-formats.mixin";
import TableRowAvatar from "@/components/work-assignments/TableRowAvatar.vue";
import bottomMenuEventBubbler from "@/mixins/menu-event-bubblers/meb-bottom.mixin.js";

export default {
  mixins: [
    themeDisabledPageMixin,
    paymentsTableFormats,
    bottomMenuEventBubbler,
  ],
  components: { TableRowAvatar },
  props: {
    item: Object,
  },
  data() {
    return {
      types: {
        msa_contractor: "MSA-Contractor",
        msa_crew: "MSA-Crew",
        gig_contract: "Gig Contract",
        gig_extension: "Gig Extension",
        w9: "W-9",
        ach_authorization: "ACH Authorization",
        other: "Other",
        ga_withholding: "GA Film Tax Credit Withholding Acknowledgement",
      },
      statuses: {
        CREATED: "Created",
        OUT_FOR_SIGNATURE: "Delivered",
        SIGNED: "Signed",
        SIGNED_OFFSITE: "Signed",
        CANCELLED: "Canceled",
        EXPIRED: "Expired",
      },
      pleaseWait: false,
    };
  },
  computed: {
    canCancel() {
      return (
        ["CREATED", "OUT_FOR_SIGNATURE"].includes(this.item.status) &&
        ["w9", "ach_authorization"].includes(this.item.agreement_type) &&
        this.item.requested_by?.id == this.currentUser.id
      );
    },
  },
  methods: {
    async cancelAgreement(item) {
      this.pleaseWait = true;
      const result = await this.$api.Agreement.cancel({ id: item.id });
      this.pleaseWait = false;
      if (result.status == 200) {
        this.$alert("Request canceled", "success");
        this.$emit("refresh");
      } else {
        this.$alert(result.data.alert || "Something went wrong", "error");
      }
    },
    async downloadPdf(item) {
      this.pleaseWait = true;
      const result = await this.$api.Agreement.documentUrl({ id: item.id });
      this.pleaseWait = false;
      if (result.status == 200) {
        window.open(result.data.url, "_blank");
      } else {
        this.$alert(result.data.alert || "Something went wrong", "error");
      }
    },
    async getSigningUrl(item) {
      this.pleaseWait = true;
      const result = await this.$api.Agreement.signingUrl({ id: item.id });
      this.pleaseWait = false;
      if (result.status == 200) {
        if (result.data?.msg == "Agreement already signed") {
          this.$alert(result.data.msg);
          this.$emit("refresh");
        } else {
          window.open(result.data.url, "_blank");
        }
      } else {
        this.$alert(result.data.alert || "Something went wrong", "error");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/payments/payments-row.scss";
@import "@/styles/agreements/payments-row.scss";

/* fml */
button.theme--dark.v-btn.v-btn--disabled.sign-btn:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: #272727 !important;
}
</style>
